import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import type { TTitleValueItem } from "@components/TitleValueList/types"
import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import { useQueryClient } from "@tanstack/react-query"
import { SquarePen } from "lucide-react"
import { useGetAddressQuery } from "@apis/address/useAddress"
import { prospectsKeys } from "@apis/prospects/queryKeys"

type TAccountUtilityData = {
  items: TTitleValueItem[]
}

export const useCasesAddressSerializer = ({
  refund_address_guid,
  prospect_guid,
  handleUpdateCaseAttribute,
}: {
  refund_address_guid: string
  prospect_guid: string
  handleUpdateCaseAttribute: (address_guid: string) => void
}) => {
  const getAddressQuery = useGetAddressQuery(refund_address_guid)
  const { closeDrawer, openDrawer } = useDrawer()
  const queryClient = useQueryClient()

  const data: TAccountUtilityData = {
    items: [
      {
        title: { content: "Refund Address:" },
        value: {
          content: (() => {
            const refundAddress = [
              getAddressQuery.data?.street,
              getAddressQuery.data?.city,
              getAddressQuery.data?.state_code,
              getAddressQuery.data?.zip,
            ]
              .filter(Boolean)
              .join(", ")

            return (
              <Button
                type="button"
                variant="link"
                className="tw:h-fit tw:p-0 tw:text-left"
                onClick={() =>
                  openDrawer(
                    <AddressInfoForm
                      identifier={prospect_guid}
                      address_guid={refund_address_guid}
                      address_type_code="prospect"
                      onSave={(address_guid) => {
                        closeDrawer()
                        queryClient.invalidateQueries({
                          queryKey: prospectsKeys.detail(prospect_guid),
                        })
                        handleUpdateCaseAttribute(address_guid)
                      }}
                      onCancel={() => closeDrawer()}
                      showMap
                    />,
                    "Update Refund Address",
                  )
                }
              >
                <span className="tw:flex tw:flex-col">
                  {refundAddress && (
                    <span className="tw:text-pretty">{refundAddress}</span>
                  )}
                  {!refundAddress && <SquarePen size={16} />}
                </span>
              </Button>
            )
          })(),
          isPending: getAddressQuery.isPending,
        },
      },
    ],
  }

  return {
    data,
    isError: getAddressQuery.isError,
  }
}
